import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { MOUNTAINS_DATA } from '../data/mountains';

export default function Home() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const stats = [
    { label: 'Global Organizations', value: '185', icon: '🌍' },
    { label: 'Strategic Careers', value: '105', icon: '💼' },
    { label: 'Mountains of Influence', value: '7', icon: '⛰️' },
  ];

  return (
    <div className="bg-black min-h-screen text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition">
            <img src="/images/fhni%20logo.png" alt="Fire Hive Network" className="h-8 w-8 object-contain" />
            <span className="text-2xl font-black">FIRE HIVE</span>
          </Link>
          <div className="flex gap-4 items-center">
            {currentUser ? (
              <>
                <span className="text-sm text-gray-400">{currentUser.email}</span>
                <Link to="/dashboard" className="bg-orange-600 hover:bg-orange-700 px-6 py-2 rounded-full font-bold transition">
                  Dashboard
                </Link>
              </>
            ) : (
              <>
                <Link to="/signin" className="text-gray-300 hover:text-white transition">Sign In</Link>
                <Link to="/signup" className="bg-orange-600 hover:bg-orange-700 px-6 py-2 rounded-full font-bold transition">
                  Join
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative min-h-screen flex items-center justify-center overflow-hidden text-center px-4 pt-32">
        <video 
          autoPlay 
          muted 
          loop 
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        >
          <source src="/videos/bgvid.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black" />
        <div className="relative z-10 flex flex-col items-center max-w-5xl">
          <h1 className="text-7xl md:text-8xl font-black mb-6 tracking-tighter uppercase leading-tight">
            Rule <span className="text-orange-500">Your World</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl">
            Raising leaders across the 7 mountains of influence to transform society globally
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            {!currentUser ? (
              <>
                <button
                  onClick={() => navigate('/signup')}
                  className="bg-white text-black px-10 py-4 rounded-full font-bold hover:bg-orange-500 hover:text-white uppercase transition text-lg"
                >
                  Join Network
                </button>
                <button
                  onClick={() => navigate('/signin')}
                  className="bg-zinc-900 border-2 border-orange-500 text-white px-10 py-4 rounded-full font-bold hover:bg-orange-500 uppercase transition text-lg"
                >
                  Sign In
                </button>
              </>
            ) : (
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-4 rounded-full font-bold uppercase transition text-lg"
              >
                Go to Dashboard
              </button>
            )}
          </div>
        </div>
      </header>

      {/* 7 Mountains Section */}
      <section className="py-24 px-6 bg-zinc-950">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-5xl font-black mb-4 text-center text-white uppercase">The 7 Mountains</h2>
          <p className="text-center text-gray-400 text-lg mb-16 max-w-2xl mx-auto">Of Influence - Spheres where leaders shape culture</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(MOUNTAINS_DATA).map(([key, mountain]) => (
              <Link
                key={key}
                to={`/mountain/${key}`}
                className="group relative h-72 rounded-lg overflow-hidden cursor-pointer border border-zinc-800 hover:border-orange-500/50 transition"
              >
                <img 
                  src={mountain.image} 
                  className="absolute inset-0 w-full h-full object-cover transition duration-700 group-hover:scale-110" 
                  alt={key}
                />
                <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center p-4 text-center transition group-hover:bg-black/80">
                  <h3 className="text-2xl font-bold text-white mb-2 uppercase capitalize">{key}</h3>
                  <p className="text-sm text-gray-200">{mountain.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-black border-t border-zinc-900">
        <div className="container mx-auto max-w-6xl grid md:grid-cols-3 gap-8">
          {stats.map((stat, i) => (
            <div key={i} className="text-center p-8 rounded-lg bg-zinc-900/50">
              <div className="text-5xl mb-3">{stat.icon}</div>
              <div className="text-5xl font-black text-orange-500 mb-2">{stat.value}</div>
              <p className="text-gray-400">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-r from-orange-600/10 to-red-600/10 border-t border-zinc-900">
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-4xl font-black mb-6 uppercase">Ready to Join?</h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Sign up today and become part of a movement to transform the 7 mountains of influence globally. Your journey starts here.
          </p>
          {!currentUser && (
            <button
              onClick={() => navigate('/signup')}
              className="bg-orange-600 hover:bg-orange-700 text-white px-10 py-4 rounded-full font-bold uppercase transition text-lg inline-block"
            >
              Get Started
            </button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-zinc-900 py-12 px-6">
        <div className="max-w-6xl mx-auto text-center text-gray-400">
          <p>&copy; 2025 Fire Hive Network. Raising leaders across the 7 mountains of influence.</p>
        </div>
      </footer>
    </div>
  );
}
